/** 통합 검색 (PRD 9장)과 방해 금지 시간 (PRD 8.3) */
import { z } from "zod";
import { isDateStr, timeToMinutes } from "./dates";
import { timeStrSchema, type TaskDTO } from "./tasks";

export const SEARCH_TYPES = ["message", "task", "wiki", "file"] as const;
export type SearchType = (typeof SEARCH_TYPES)[number];

const TYPE_ALIASES: Record<string, SearchType> = {
  message: "message",
  msg: "message",
  메시지: "message",
  task: "task",
  할일: "task",
  wiki: "wiki",
  위키: "wiki",
  file: "file",
  파일: "file",
};

export interface ParsedSearch {
  /** 모두 들어 있어야 하는 낱말 ("따옴표"로 묶으면 한 낱말) */
  terms: string[];
  /** in:#채널 */
  channel: string | null;
  /** from:@사람 (아이디 또는 이름) */
  from: string | null;
  /** before:YYYY-MM-DD (그날은 빼고), after:YYYY-MM-DD (그날부터) */
  before: string | null;
  after: string | null;
  /** is:task, is:wiki, is:message, is:file */
  type: SearchType | null;
}

/** "견적 in:#영업 from:@minsu after:2026-10-01 is:task" */
export function parseSearch(q: string): ParsedSearch {
  const out: ParsedSearch = { terms: [], channel: null, from: null, before: null, after: null, type: null };
  const tokens = q.match(/"[^"]*"|\S+/g) ?? [];
  for (const raw of tokens) {
    if (raw.startsWith('"')) {
      const phrase = raw.slice(1, -1).trim();
      if (phrase) out.terms.push(phrase);
      continue;
    }
    const m = /^(in|from|before|after|is):(.+)$/i.exec(raw);
    const key = m?.[1]!.toLowerCase();
    const value = m?.[2]!;
    if (key === "in" && value) out.channel = value.replace(/^#/, "").toLowerCase();
    else if (key === "from" && value) out.from = value.replace(/^@/, "");
    else if ((key === "before" || key === "after") && isDateStr(value)) out[key] = value;
    else if (key === "is" && TYPE_ALIASES[value.toLowerCase()]) out.type = TYPE_ALIASES[value.toLowerCase()]!;
    else out.terms.push(raw);
  }
  return out;
}

export const hasSearchCondition = (p: ParsedSearch) => p.terms.length > 0 || !!p.channel || !!p.from || !!p.before || !!p.after;

/** 검색 결과에 보여줄 글: 마크다운 기호를 걷어낸다 (링크는 글자만, 위키 링크는 별칭이나 제목) */
export function markdownToPlain(md: string): string {
  return md
    .replace(/^```.*$/gm, "")
    .replace(/!\[([^\]]*)\]\([^)]*\)/g, "$1")
    .replace(/\[\[([^\]|\n]+)\|([^\]\n]+)\]\]/g, "$2")
    .replace(/\[\[([^\]\n]+)\]\]/g, "$1")
    .replace(/\[([^\]\n]+)\]\([^)]*\)/g, "$1")
    .replace(/^\s{0,3}#{1,6}\s+/gm, "")
    .replace(/^\s*(?:>\s?)+/gm, "")
    .replace(/^\s*(?:[-*+]|\d+[.)])\s+(?:\[[ xX]\]\s+)?/gm, "")
    .replace(/^\s*[-*_]{3,}\s*$/gm, "")
    .replace(/[*_~`|]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

/** 일치하는 곳 앞뒤를 잘라 보여준다 */
export function snippetAround(text: string, terms: string[], radius = 60): string {
  const flat = text.replace(/\s+/g, " ").trim();
  const lower = flat.toLowerCase();
  const at = terms.map((t) => lower.indexOf(t.toLowerCase())).filter((i) => i >= 0).sort((a, b) => a - b)[0] ?? 0;
  const start = Math.max(0, at - radius);
  const end = Math.min(flat.length, at + radius * 2);
  return `${start > 0 ? "…" : ""}${flat.slice(start, end)}${end < flat.length ? "…" : ""}`;
}

export interface SearchMessageHit {
  id: number;
  channelId: number | null;
  conversationId: number | null;
  parentId: number | null;
  authorId: number | null;
  createdAt: string;
  snippet: string;
}

export interface SearchWikiHit {
  id: number;
  spaceId: number;
  title: string;
  status: "draft" | "published";
  snippet: string;
  updatedAt: string;
}

export interface SearchFileHit {
  id: number;
  filename: string;
  mime: string;
  size: number;
  url: string;
  isImage: boolean;
  thumbUrl: string | null;
  messageId: number;
  channelId: number | null;
  conversationId: number | null;
  parentId: number | null;
  authorId: number | null;
  createdAt: string;
}

export interface SearchResultDTO {
  query: ParsedSearch;
  /** in:·from:에 쓴 채널·사람을 찾지 못했으면 그 설명 */
  notice: string | null;
  messages: { items: SearchMessageHit[]; total: number };
  tasks: { items: TaskDTO[]; total: number };
  wiki: { items: SearchWikiHit[]; total: number };
  files: { items: SearchFileHit[]; total: number };
}

/* ───────────── 방해 금지 ───────────── */

/** null이면 끈다 */
export const quietHoursInput = z.object({ quietHours: z.object({ start: timeStrSchema, end: timeStrSchema }).nullable() });

/** 지금(하루 중 분)이 방해 금지 시간인지. 22:00~08:00처럼 자정을 넘겨도 된다. 시작과 끝이 같으면 꺼진 것으로 본다 */
export function inQuietHours(start: string | null, end: string | null, minutes: number): boolean {
  if (!start || !end || start === end) return false;
  const s = timeToMinutes(start);
  const e = timeToMinutes(end);
  return s < e ? minutes >= s && minutes < e : minutes >= s || minutes < e;
}
